import React from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  FiGrid,
  FiUserPlus,
  FiTruck,
  FiUsers,
  FiBox,
  FiShoppingCart,
  FiDollarSign,
  FiBarChart2,
  FiFolder,
  FiLayers,
  FiClock,
  FiMaximize,
  FiX,
} from "react-icons/fi";

const Sidebar = ({ isOpen, onCloseMobile }) => {
  const { isAdmin, isSuperAdmin, hasPermission } = useAuth();

  // Navigation Items defined with granular permissions
  const navItems = [
    {
      to: "/dashboard",
      label: "Dashboard",
      icon: FiGrid,
      adminOnly: false,
      permission: "dashboard",
    },
    {
      to: "/users",
      label: "Add User",
      subLabel: "User Management",
      icon: FiUserPlus,
      adminOnly: true, // RBAC: Admin & Super Admin
    },
    {
      to: "/vendors",
      label: "Add Vendor",
      subLabel: "Vendors & Suppliers",
      icon: FiTruck,
      adminOnly: false,
      permission: "vendors",
    },
    {
      to: "/categories",
      label: "Categories",
      subLabel: "Cat & Sub-category",
      icon: FiFolder,
      adminOnly: false,
      permission: "categories",
    },
    {
      to: "/inventory",
      label: "Inventory",
      subLabel: "Items & Catalog",
      icon: FiBox,
      adminOnly: false,
      permission: "inventory",
    },
    {
      to: "/purchases",
      label: "Purchases",
      subLabel: "Vendor Purchase Orders",
      icon: FiShoppingCart,
      adminOnly: false,
      permission: "purchases",
    },
    {
      to: "/clients",
      label: "Add Client",
      subLabel: "Customers & Clients",
      icon: FiUsers,
      adminOnly: false,
      permission: "clients",
    },
    {
      to: "/sales",
      label: "Sales",
      subLabel: "Client Sales Orders",
      icon: FiDollarSign,
      adminOnly: false,
      permission: "sales",
    },
    {
      to: "/stock",
      label: "Stock",
      subLabel: "Available Stock",
      icon: FiLayers,
      adminOnly: false,
      permission: "stock",
    },
    {
      to: "/scan",
      label: "Scan",
      subLabel: "Device & Camera Scanner",
      icon: FiMaximize,
      superAdminOnly: true, // Exclusive: Super Admin only
    },
    {
      to: "/warranty",
      label: "Warranty Expire",
      subLabel: "Tracking & Alerts",
      icon: FiClock,
      adminOnly: false,
      permission: "warranty",
    },
    {
      to: "/reports",
      label: "Reports",
      subLabel: "Stock Audit & Staff Activity",
      icon: FiBarChart2,
      adminOnly: true, // Only shown to Admin
      permission: "reports",
    },
  ];

  // Filter items based on Role-Based Access Control and Granular Permissions
  const filteredNavItems = navItems.filter((item) => {
    // Items restricted to Super Admin only are visible ONLY to Super Admin
    if (item.superAdminOnly) {
      return isSuperAdmin;
    }
    if (isSuperAdmin || isAdmin) return true;
    if (item.adminOnly) return false;
    if (item.permission && !hasPermission(item.permission)) return false;
    return true;
  });

  return (
    <>
      {/* Mobile backdrop (only visible on mobile screens, hidden on desktop, no blur) */}
      <div
        className={`sidebar-mobile-backdrop ${isOpen ? "active" : ""}`}
        onClick={onCloseMobile}
      />

      <aside
        className={`app-sidebar ${isOpen ? "open" : "collapsed"}`}
        style={{
          width: isOpen ? 260 : 78,
          backgroundColor: "var(--navy, #0F172A)",
          color: "#ffffff",
          display: "flex",
          flexDirection: "column",
          transition: "width 0.25s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          overflowX: "hidden",
          overflowY: "auto",
          zIndex: 95,
          borderRight: "1px solid rgba(255, 255, 255, 0.08)",
          flexShrink: 0,
        }}
      >
        {/* Mobile Header (only visible on small/mobile screens) */}
        <div className="sidebar-mobile-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "linear-gradient(135deg, #064E3B 0%, #10B981 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <FiLayers size={18} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: "0.95rem", color: "#fff" }}>
                Commercial <span style={{ color: "var(--accent)" }}>IT</span>
              </div>
              <div style={{ fontSize: "0.68rem", color: "var(--slate-400)" }}>Navigation Menu</div>
            </div>
          </div>
          <button
            onClick={onCloseMobile}
            aria-label="Close menu"
            className="sidebar-close-btn"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Navigation list */}
        <div style={{ padding: "16px 10px", display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
          <div
            style={{
              padding: isOpen ? "4px 12px 10px" : "4px 0 10px",
              fontSize: "0.68rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--slate-400)",
              textAlign: isOpen ? "left" : "center",
            }}
          >
            {isOpen ? "Main Navigation" : "•••"}
          </div>

          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onCloseMobile}
                style={({ isActive }) => ({
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: isOpen ? "10px 14px" : "12px 0",
                  justifyContent: isOpen ? "flex-start" : "center",
                  borderRadius: "var(--radius-md)",
                  color: isActive ? "#ffffff" : "var(--slate-300)",
                  backgroundColor: isActive ? "var(--primary)" : "transparent",
                  boxShadow: isActive ? "0 4px 12px var(--accent-glow)" : "none",
                  fontWeight: isActive ? 700 : 500,
                  fontSize: "0.88rem",
                  transition: "all 0.18s ease-in-out",
                  position: "relative",
                })}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: 24 }}>
                  <Icon size={20} />
                </div>
                {isOpen && (
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {item.label}
                    </div>
                    {item.subLabel && (
                      <div style={{ fontSize: "0.7rem", color: "var(--slate-400)", marginTop: 2, whiteSpace: "nowrap" }}>
                        {item.subLabel}
                      </div>
                    )}
                  </div>
                )}
                {isOpen && item.superAdminOnly && (
                  <span
                    style={{
                      fontSize: "0.62rem",
                      padding: "2px 6px",
                      borderRadius: 4,
                      backgroundColor: "rgba(16, 185, 129, 0.2)",
                      color: "#6EE7B7",
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    Super Admin
                  </span>
                )}
                {isOpen && !item.superAdminOnly && item.adminOnly && (
                  <span
                    style={{
                      fontSize: "0.65rem",
                      padding: "2px 6px",
                      borderRadius: 4,
                      backgroundColor: "rgba(220, 38, 38, 0.2)",
                      color: "#FCA5A5",
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    Admin
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>

        {/* Footer info in sidebar */}
        {isOpen && (
          <div
            style={{
              padding: "16px 18px",
              borderTop: "1px solid rgba(255, 255, 255, 0.08)",
              backgroundColor: "rgba(0, 0, 0, 0.2)",
              fontSize: "0.75rem",
              color: "var(--slate-400)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "var(--accent)" }} />
              <span style={{ color: "#fff", fontWeight: 600 }}>System Live</span>
            </div>
            <div style={{ marginTop: 4, fontSize: "0.68rem" }}>Commercial IT Inventory v1.0</div>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
